import { PDFDocument, StandardFonts, rgb, PDFFont, PDFPage, PDFForm } from 'pdf-lib';
import { format } from 'date-fns';
import { calculateAutoSizeFontSize, createPdfFontMeasurer, createPdfRichTextMeasurer, calculateFormattedAutoSizeFontSize, DEFAULT_MIN_FONT_SIZE, DEFAULT_LINE_HEIGHT_RATIO, FIELD_PADDING } from '../utils/pdfHelpers';
import { containsRichTextTags, parseRichText } from '../utils/richTextParser';

export interface PDFGenerationOptions {
  filename?: string;
  headerConfigurationJson?: string; // JSON config for dynamic header generation
  headerHeight?: number;
  pageMargin?: number;
  contentMargin?: number;
  headerBottomMargin?: number;
  // Form layout configuration
  numberOfColumns?: number; // Number of columns in the form layout (default: 2)
  // Font size configuration
  defaultFontSize?: number; // Default font size for text fields (default: 7)
  labelFontSize?: number; // Font size for labels (default: 8)
  multilineFontSize?: number; // Font size for multiline fields (default: 7)
  sectionTitleFontSize?: number; // Font size for section titles (default: 12)
  // Padding and spacing configuration
  fieldSpacing?: number; // Spacing between fields (default: 3.5)
  sectionSpacing?: number; // Spacing between sections (default: 5)
  checkboxListSpacing?: number; // Extra spacing under checkbox lists (default: 5)
  checkboxListColumns?: number; // Number of columns for checkbox lists (default: 3)
  sectionPadding?: number; // Padding inside sections (default: 3)
  fieldHeight?: number; // Height of standard fields (default: 16)
  // Section styling
  showSectionBorders?: boolean; // Whether to show section borders (default: true)
  // Dynamic header data (for programmatic values)
  headerData?: { [key: string]: string | number | boolean | null | undefined }; // Data to populate header placeholders
  // Pixel Version flag
  pixelVersion?: boolean; // If true, use enhanced configuration from FormFields and FormSections (default: false)
  // Interactive view flag
  isInteractiveView?: boolean; // If true, don't pre-populate checkboxes (let overlay handle state)
  // Data for parameter preloading
  job?: any;
  customer?: any;
  firstAppointment?: any; // First appointment data for parameter injection
  formParameterValues?: { [fieldId: number]: string };
  userDataFields?: any[]; // Store fetched user data fields for parameter injection
}

// Header Configuration Interfaces
export interface HeaderConfiguration {
  type: 'dynamic' | 'image';
  height?: number;
  backgroundColor?: string;
  rightPadding?: number; // Padding from right edge for right-aligned elements
  elements: HeaderElement[];
}

export interface TextElement {
  type: 'text';
  content: string;
  fontSize: number;
  fontWeight?: 'normal' | 'bold';
  fontFamily?: string;
  color: string;
  x: 'center' | 'left' | 'right' | 'margin' | number;
  y: number;
  alignment?: 'left' | 'center' | 'right';
  maxWidth?: number;
}

export interface LineElement {
  type: 'line';
  x1: 'margin' | 'page-width-minus-margin' | number;
  y1: number;
  x2: 'margin' | 'page-width-minus-margin' | number;
  y2: number;
  thickness: number;
  color: string;
  dashPattern?: number[];
}

export interface RectangleElement {
  type: 'rectangle';
  x: number;
  y: number;
  width: number | 'page-width-minus-margin';
  height: number;
  fillColor?: string;
  borderColor?: string;
  borderWidth?: number;
}

export type HeaderElement = TextElement | LineElement | RectangleElement;

export class PDFService {
  /**
   * Normalizes fieldType to handle both string and numeric enum values
   */
  private static normalizeFieldType(fieldType: any): string {
    // If it's already a string, return it
    if (typeof fieldType === 'string') {
      if (fieldType === 'Divider') return 'HorizontalRule';
      return fieldType;
    }
    
    // If it's a number (enum value), convert to string name
    const enumMap: { [key: number]: string } = {
      1: 'String',
      2: 'Multiline',
      3: 'Bool',
      4: 'Dimension',
      5: 'Int',
      6: 'Dropdown',
      7: 'Date',
      8: 'DateTime',
      9: 'CheckboxList',
      10: 'HorizontalRule',
      11: 'TextboxWithCheckbox'
    };
    
    return enumMap[fieldType as number] || 'String';
  }


  /**
   * Wraps text to fit within a maximum width
   */
  private static wrapText(text: string, maxWidth: number, font: PDFFont, fontSize: number): string[] {
    if (!text) return [];

    // Split by explicit newlines first
    const paragraphs = text.split(/\r?\n/);
    const allLines: string[] = [];

    for (const paragraph of paragraphs) {
      const words = paragraph.split(' ');
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        if (font.widthOfTextAtSize(testLine, fontSize) <= maxWidth) {
          currentLine = testLine;
        } else {
          if (currentLine) allLines.push(currentLine);
          currentLine = word;
        }
      }
      if (currentLine) allLines.push(currentLine);
    }

    return allLines;
  }

  /**
   * Parses a field's configuration JSON into an object.
   */
  private static parseFieldConfiguration(field: any): Record<string, any> {
    if (!field || !field.configurationJson) {
      return {};
    }

    if (typeof field.configurationJson === 'object') {
      return field.configurationJson as Record<string, any>;
    }

    try {
      return JSON.parse(field.configurationJson) as Record<string, any>;
    } catch (error) {
      return {};
    }
  }


  /**
   * Generates a truly interactive PDF with editable form fields using pdf-lib
   * Returns the PDF as a Uint8Array so the caller can decide whether to download or display it
   * 
   * When options.pixelVersion is true, the system uses enhanced configuration from:
   * - FormFields.ConfigurationJson (parsed via parseFieldConfiguration)
   * - FormSections.ConfigurationJson (used for layout and positioning)
   * This enables advanced field customization including fontSize, textAlign, fieldOnNextLine,
   * displayAsLabel, multilineLines, checkboxColumns, and more.
   */
  static async generateEditablePDF(
    formData: any,
    formTitle: string,
    formSections: any[],
    options: PDFGenerationOptions = {}
  ): Promise<Uint8Array> {
    const {
      filename = 'form.pdf',
      headerConfigurationJson,
      headerHeight = 100,
      pageMargin = 15,
      headerBottomMargin = 20,
      headerData = {},
      // Form layout configuration
      numberOfColumns = 2,
      // Font size configuration with defaults
      defaultFontSize = 10,
      labelFontSize = 8.5,
      multilineFontSize = 10,
      sectionTitleFontSize = 12,
      // Padding and spacing configuration with defaults
      fieldSpacing = 3.5,
      sectionSpacing = 12,
      checkboxListSpacing = -7,
      checkboxListColumns = 3,
      sectionPadding = 3,
      fieldHeight: configFieldHeight = 16
    } = options;

    const showSectionBorders = options.showSectionBorders ?? true;

    try {
      // Create a new PDF document
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595, 842]); // A4 size in points
      const { width: pageWidth, height: pageHeight } = page.getSize();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const form = pdfDoc.getForm();

      // Populate headerData with user data fields passed by the caller.
      // The caller (e.g. JobDetailPage) is responsible for providing the correct
      // user's fields (assigned salesperson), so we never fall back to the current
      // logged-in user here — that would show the wrong person's data.
      if (headerConfigurationJson && options.userDataFields && options.userDataFields.length > 0) {
        options.userDataFields.forEach((field: any) => {
          if (field.businessDataField?.code) {
            headerData[field.businessDataField.code] = field.value;
          }
        });
      }

      // Add header - prefer dynamic header configuration
      let headerImgHeight = 0;
      if (headerConfigurationJson) {
        try {
          const headerConfig = JSON.parse(headerConfigurationJson) as HeaderConfiguration;
          if (headerConfig.type === 'dynamic' && headerConfig.elements) {
            headerImgHeight = await this.renderDynamicHeader(
              page,
              pdfDoc,
              headerConfig,
              pageWidth,
              pageHeight,
              pageMargin,
              headerHeight || headerConfig.height || 100,
              font,
              boldFont,
              headerData
            );
          }
        } catch {
          // Invalid header configuration JSON
        }
      }

      // Sort sections by display order
      const sortedSections = formSections
        .filter((section: any) => section.isActive)
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

      // Render using PixelVersion (absolute positioning)
      await this.renderPixelVersion(
        sortedSections,
        pdfDoc,
        page,
        headerConfigurationJson,
        headerHeight,
        pageMargin,
        headerData,
        font,
        boldFont,
        form,
        formData,
        pageWidth,
        pageHeight,
        defaultFontSize,
        labelFontSize,
        options
      );

      const pdfBytes = await pdfDoc.save();
      return pdfBytes;
    } catch (error) {
      throw new Error('Failed to generate editable PDF');
    }
  }


  /**
   * Renders a dynamic header based on configuration
   * @returns The actual height of the rendered header
   */
  private static async renderDynamicHeader(
    page: PDFPage,
    pdfDoc: PDFDocument,
    headerConfig: HeaderConfiguration,
    pageWidth: number,
    pageHeight: number,
    pageMargin: number,
    headerHeight: number,
    font: PDFFont,
    boldFont: PDFFont,
    headerData: { [key: string]: string | number | boolean | null | undefined } = {}
  ): Promise<number> {
    const actualHeaderHeight = headerConfig.height || headerHeight;
    const headerTopY = pageHeight - pageMargin;
    const headerBottomY = headerTopY - actualHeaderHeight;
    
    
    // Draw background if specified
    if (headerConfig.backgroundColor) {
      const bgColor = this.parseColor(headerConfig.backgroundColor);
      page.drawRectangle({
        x: pageMargin,
        y: headerBottomY,
        width: pageWidth - (pageMargin * 2),
        height: actualHeaderHeight,
        color: bgColor,
        borderWidth: 0,
      });
    }
    
    // Helper function to replace placeholders in content (e.g., {{DisplayName}})
    const replacePlaceholders = (content: string): string => {
      return content.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        const value = headerData[key];
        return value !== undefined && value !== null ? String(value) : match;
      });
    };
    
    // Helper function to calculate X position
    const calculateX = (x: 'center' | 'left' | 'right' | 'margin' | number, contentWidth?: number): number => {
      if (typeof x === 'number') {
        return pageMargin + x;
      }
      switch (x) {
        case 'center':
          return contentWidth ? (pageWidth - contentWidth) / 2 : pageWidth / 2;
        case 'left':
          return pageMargin;
        case 'right': {
          const rightPadding = headerConfig.rightPadding || 0;
          return contentWidth 
            ? pageWidth - pageMargin - rightPadding - contentWidth 
            : pageWidth - pageMargin - rightPadding;
        }
        case 'margin':
          return pageMargin;
        default:
          return pageMargin;
      }
    };
    
    // Helper function to calculate X2 position for lines
    const calculateX2 = (x: 'margin' | 'page-width-minus-margin' | number): number => {
      if (typeof x === 'number') {
        return pageMargin + x;
      }
      switch (x) {
        case 'margin':
          return pageMargin;
        case 'page-width-minus-margin':
          return pageWidth - pageMargin;
        default:
          return pageWidth - pageMargin;
      }
    };
    
    // Render each element
    for (const element of headerConfig.elements) {
      switch (element.type) {
        case 'text': {
          const content = replacePlaceholders(element.content);
          const elementFont = element.fontWeight === 'bold' ? boldFont : font;
          const fontSize = element.fontSize;
          const textWidth = elementFont.widthOfTextAtSize(content, fontSize);
          const textColor = this.parseColor(element.color);
          
          // Calculate X position
          let xPos = calculateX(element.x, textWidth);
          
          // If alignment is specified, adjust X position only if x is a string keyword
          // If x is a number, preserve it as an offset from the margin
          if (element.alignment && typeof element.x !== 'number') {
            if (element.alignment === 'left') {
              xPos = calculateX('left');
            } else if (element.alignment === 'right') {
              xPos = calculateX('right', textWidth);
            } else if (element.alignment === 'center') {
              xPos = calculateX('center', textWidth);
            }
          }
          
          // Calculate Y position (from top of header)
          const yPos = headerTopY - element.y - fontSize;
          
          
          // Draw text
          page.drawText(content, {
            x: xPos,
            y: yPos,
            size: fontSize,
            font: elementFont,
            color: textColor,
          });
          
          break;
        }
        
        case 'line': {
          const x1 = calculateX2(element.x1);
          const y1 = headerTopY - element.y1;
          const x2 = calculateX2(element.x2);
          const y2 = headerTopY - element.y2;
          const lineColor = this.parseColor(element.color);
          
          page.drawLine({
            start: { x: x1, y: y1 },
            end: { x: x2, y: y2 },
            thickness: element.thickness,
            color: lineColor,
          });
          
          break;
        }
        
        case 'rectangle': {
          const x = pageMargin + element.x;
          const y = headerTopY - element.y - element.height;
          const fillColor = element.fillColor ? this.parseColor(element.fillColor) : undefined;
          const borderColor = element.borderColor ? this.parseColor(element.borderColor) : undefined;
          
          // Calculate width - support string values like 'page-width-minus-margin'
          let width: number;
          if (typeof element.width === 'string') {
            if (element.width === 'page-width-minus-margin') {
              width = pageWidth - (pageMargin * 2);
            } else {
              width = element.width as any; // Fallback (shouldn't happen)
            }
          } else {
            width = element.width;
          }
          
          page.drawRectangle({
            x,
            y,
            width,
            height: element.height,
            color: fillColor,
            borderColor: borderColor,
            borderWidth: element.borderWidth || 0,
          });
          
          break;
        }
      }
    }
    
    return actualHeaderHeight;
  }
  
  /**
   * Parses a color string (hex, rgb, or named color) to RGB values
   */
  private static parseColor(colorString: string): ReturnType<typeof rgb> {
    // Hex color (#RRGGBB or #RGB)
    if (colorString.startsWith('#')) {
      const hex = colorString.slice(1);
      const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.slice(4, 6), 16) / 255;
      return rgb(r, g, b);
    }
    
    // RGB color (rgb(r, g, b))
    const rgbMatch = colorString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]) / 255;
      const g = parseInt(rgbMatch[2]) / 255;
      const b = parseInt(rgbMatch[3]) / 255;
      return rgb(r, g, b);
    }
    
    // Named colors (basic set)
    const namedColors: { [key: string]: ReturnType<typeof rgb> } = {
      black: rgb(0, 0, 0),
      white: rgb(1, 1, 1),
      red: rgb(1, 0, 0),
      green: rgb(0, 1, 0),
      blue: rgb(0, 0, 1),
      gray: rgb(0.5, 0.5, 0.5),
      grey: rgb(0.5, 0.5, 0.5),
    };
    
    const lowerColor = colorString.toLowerCase();
    if (namedColors[lowerColor]) {
      return namedColors[lowerColor];
    }
    
    // Default to black if color can't be parsed
    return rgb(0, 0, 0);
  }

  /**
   * Adds header to a page (dynamic header or image)
   */
  private static async addHeaderToPage(
    page: PDFPage,
    pdfDoc: PDFDocument,
    headerConfigurationJson: string | undefined,
    pageWidth: number,
    pageHeight: number,
    pageMargin: number,
    headerHeight: number,
    font: PDFFont,
    boldFont: PDFFont,
    headerData: { [key: string]: string | number | boolean | null | undefined } = {}
  ): Promise<number> {
    // Prefer dynamic header configuration, fallback to image
    if (headerConfigurationJson) {
      try {
        const headerConfig = JSON.parse(headerConfigurationJson) as HeaderConfiguration;
        if (headerConfig.type === 'dynamic' && headerConfig.elements) {
          return await this.renderDynamicHeader(
            page,
            pdfDoc,
            headerConfig,
            pageWidth,
            pageHeight,
            pageMargin,
            headerHeight || headerConfig.height || 100,
            font,
            boldFont,
            headerData
          );
        }
      } catch (error) {
      }
    }

    // No header image functionality - return 0
    return 0;
  }


  /**
   * Renders the PDF using absolute positioning based on configuration (Pixel Version)
   */
  private static async renderPixelVersion(
    sections: any[],
    pdfDoc: PDFDocument,
    firstPage: PDFPage,
    headerConfigurationJson: string | undefined,
    headerHeight: number,
    pageMargin: number,
    headerData: any,
    font: PDFFont,
    boldFont: PDFFont,
    form: PDFForm,
    formData: any,
    pageWidth: number,
    pageHeight: number,
    defaultFontSize: number,
    labelFontSize: number,
    options: PDFGenerationOptions
  ): Promise<void> {

    // Define items to render (Sections and Fields)
    interface PDFItem {
      type: 'field' | 'section';
      data: any; // field or section object
      config: any;
      pageNumber: number;
    }

    const allItems: PDFItem[] = [];

    sections.forEach(section => {
      // 1. Process Section Config (Border, Background)
      if (section.isActive) {
        const config = this.parseFieldConfiguration(section);
        // Priority: JSON config > DTO property > flat config
        // const pdfConfig = config.pdfConfig || section.pdfConfig || config;


        let pdfConfig: any = null;
        if (config && typeof config === 'object' && config.pdfConfig) {
          pdfConfig = { ...config, ...config.pdfConfig };
        } else if (section.pdfConfig) {
          pdfConfig = { ...config, ...section.pdfConfig };
        } else if (config && (config.x !== undefined || config.y !== undefined)) {
          pdfConfig = config;
        }

        if (pdfConfig) {
          // Parse page number safely
          const rawPageNum = pdfConfig.pageNumber;
          const pageNum = rawPageNum ? parseInt(rawPageNum.toString(), 10) : 1;

          if (!isNaN(pageNum) && pageNum > 0) {
            allItems.push({ type: 'section', data: section, config: pdfConfig, pageNumber: pageNum });
          }
        }
      }

      // 2. Process Fields in Section
      // 2. Process Fields in Section
      if (section.formFields) {
        section.formFields.forEach((field: any) => {
          if (field.isActive) {
            let config = this.parseFieldConfiguration(field);

            // Debug parsing for BIN_SYSTEMS
            if (field.code === 'BIN_SYSTEMS') {
            }

            // Priority logic:
            // 1. config.pdfConfig (from JSON string in DB) - "Source of Truth"
            // 2. field.pdfConfig (from DTO property) - Fallback
            // 3. config (flat properties in JSON) - Legacy/Fallback

            let pdfConfig: any = null;
            let configSource = 'unknown';

            // Check if 'config' has a nested 'pdfConfig' object
            if (config && typeof config === 'object' && config.pdfConfig && typeof config.pdfConfig === 'object') {
              pdfConfig = { ...config, ...config.pdfConfig };
              configSource = 'json_nested_merged';
            }
            // Check if field DTO has 'pdfConfig' property
            else if (field.pdfConfig && typeof field.pdfConfig === 'object') {
              pdfConfig = { ...config, ...field.pdfConfig };
              configSource = 'dto_property_merged';
            }
            // Fallback to flat config if it has coordinate properties
            else if (config && (config.x !== undefined || config.y !== undefined)) {
              pdfConfig = config;
              configSource = 'json_flat';
            }
            else {
              // Last resort
              pdfConfig = {};
              configSource = 'empty';
            }

            // Parse page number safely
            const rawPageNum = pdfConfig.pageNumber;
            const pageNum = rawPageNum ? parseInt(rawPageNum.toString(), 10) : 1;

            if (field.code === 'BIN_SYSTEMS') {
            }

            allItems.push({ type: 'field', data: field, config: pdfConfig, pageNumber: (isNaN(pageNum) || pageNum < 1) ? 1 : pageNum });
          }
        });
      }
    });


    // Group items by page number
    const itemsByPage: { [key: number]: PDFItem[] } = {};
    let maxPage = 1;

    allItems.forEach(item => {
      const pageNum = item.pageNumber;
      if (!itemsByPage[pageNum]) {
        itemsByPage[pageNum] = [];
      }
      itemsByPage[pageNum].push(item);
      if (pageNum > maxPage) maxPage = pageNum;
    });


    // Iterate through pages
    for (let pageNum = 1; pageNum <= maxPage; pageNum++) {
      let page: PDFPage;

      if (pageNum === 1) {
        page = firstPage;
        // Fix for "Inverted/Upside Down" content on Page 1.
        // Ensure rotation is 0. 
        //page.setRotation(degrees(0));
      } else {
        page = pdfDoc.addPage([595, 842]);

        await this.addHeaderToPage(
          page,
          pdfDoc,
          headerConfigurationJson,
          pageWidth,
          pageHeight,
          pageMargin,
          headerHeight,
          font,
          boldFont,
          headerData
        );
      }

      const pageItems = itemsByPage[pageNum] || [];
      // Sort: Sections first (background), then fields
      pageItems.sort((a, b) => {
        if (a.type === 'section' && b.type !== 'section') return -1;
        if (a.type !== 'section' && b.type === 'section') return 1;
        return 0;
      });


      for (const item of pageItems) {
        if (item.type === 'section') {
          await this.drawPixelVersionSection(page, item.data, item.config, pageHeight, boldFont);
        } else {

          // Normalize Global Y Coordinates
          // If Y > PageHeight, it implies a Global Coordinate was saved.
          // We map it to the current page.
          // If pageNum is 1, Y shouldn't be > pageHeight usually.
          // If pageNum is 2, Y might look like 897.
          // We want relative Y (55).
          // Formula: relativeY = globalY - (pageHeight * (pageNum - 1)).
          // But we need to use 'pageNum' derived from the ITEM, or the loop?
          // The item *is* in `itemsByPage[pageNum]`. So `pageNum` is correct context.

          // However, sometimes Y might be "810" on Page 2? (Bottom-aligned?)
          // No, "810" is < 842.
          // Only fix if > 842.

          // Start with simple modulo-like fix if plausible.
          // 897 on Page 2 (842 height) -> 55.
          if (item.config && typeof item.config.y === 'number' && item.config.y > pageHeight) {
            const originalY = item.config.y;
            item.config.y = item.config.y - (pageHeight * (pageNum - 1));


            // Also fix Label Y if it exists and is Global
            if (item.config.labelConfig && typeof item.config.labelConfig.y === 'number' && item.config.labelConfig.y > pageHeight) {
              item.config.labelConfig.y = item.config.labelConfig.y - (pageHeight * (pageNum - 1));
            }
          }

          const debugY = item.config?.y !== undefined ? item.config.y : 'undefined';
          const debugX = item.config?.x !== undefined ? item.config.x : 'undefined';


          await this.addAbsoluteFieldToPDF(
            page,
            form,
            item.data,
            item.config,
            formData,
            font,
            boldFont,
            pageHeight,
            defaultFontSize,
            labelFontSize,
            options
          );
        }
      }
    }
  }

  /**
   * Draws a section rectangle/border
   */
  private static async drawPixelVersionSection(
    page: PDFPage,
    section: any,
    config: any,
    pageHeight: number,
    font: PDFFont
  ): Promise<void> {
    if (!config) return;

    const { x, y, width, height } = config;
    if (x === undefined || y === undefined || width === undefined || height === undefined) {
      return;
    }

    // Check if border should be shown: obey explicit "false", otherwise allow if true or if border width is positive
    const shouldShowBorder = config.showBorder !== false &&
      (config.showBorder === true || (config.borderWidth !== undefined && config.borderWidth > 0));


    const pdfY = pageHeight - y - height;

    // Determine if we have any fill color
    const fillColorString = config.backgroundColor || config.fillColor;
    const fillColor = fillColorString ? this.parseColor(fillColorString) : undefined;

    if (shouldShowBorder) {
      // Draw with border
      const borderWidth = config.borderWidth || 1;
      const borderColorString = config.borderColor || '#cccccc';
      const borderColor = this.parseColor(borderColorString);

      page.drawRectangle({
        x: x,
        y: pdfY,
        width: width,
        height: height,
        borderWidth: borderWidth,
        borderColor: borderColor,
        color: fillColor,
      });

      // Render section description as a label on the top-left border
      if (section && section.description && section.description.trim() !== '') {
        const description = section.description.trim();
        const fontSize = 10; // Small label size
        const textWidth = font.widthOfTextAtSize(description, fontSize);
        const textHeight = fontSize * 0.8; // Approximate cap height for centering

        // Background rectangle for text (to "cut" the border)
        const bgPaddingX = 4;
        const bgPaddingY = 2;
        const bgWidth = textWidth + (bgPaddingX * 2);
        const bgHeight = fontSize + (bgPaddingY * 2);

        // Position it on the top border
        // Top border is at pdfY + height
        const labelX = x + 10; // offset from left border
        const labelYCenter = pdfY + height;
        const labelRectY = labelYCenter - (bgHeight / 2);

        // Draw white background
        page.drawRectangle({
          x: labelX,
          y: labelRectY,
          width: bgWidth,
          height: 8,
          color: rgb(1, 1, 1), // White
        });

        // Draw centered text
        // Baseline adjustment: drawText Y is the baseline. 
        // To center it, we start from labelYCenter and subtract half the x-height/cap-height roughly.
        page.drawText(description, {
          x: labelX + bgPaddingX,
          y: labelYCenter - (textHeight / 2) - 1,
          size: fontSize,
          font: font,
          color: rgb(0, 0, 0),
        });
      }
    } else if (fillColor) {
      // Draw background only (no border)
      page.drawRectangle({
        x: x,
        y: pdfY,
        width: width,
        height: height,
        color: fillColor,
        borderWidth: 0
      });
    }
  }

  /**
   * Adds a field to the PDF using absolute positioning
   */
  private static async addAbsoluteFieldToPDF(
    page: PDFPage,
    form: PDFForm,
    field: any,
    config: any,
    formData: any,
    font: PDFFont,
    boldFont: PDFFont,
    pageHeight: number,
    defaultFontSize: number,
    labelFontSize: number,
    options: PDFGenerationOptions
  ): Promise<void> {
    const fieldType = this.normalizeFieldType(field.fieldType);

    // Ensure coordinates are numbers
    const x = Number(config.x) || 50;
    const y = Number(config.y) || 100;
    const width = Number(config.width) || 150;
    const height = Number(config.height) || 25;

    // Convert top-left Y (input) to bottom-left Y (PDF)
    // Input Y=0 is Top. PDF Y=0 is Bottom.
    const pdfY = pageHeight - y - height;


    // Draw Label (if configured)
    if (config.labelConfig && config.labelConfig.isVisible !== false) {
      // Handle hideColon - default to showing colon unless explicitly hidden
      const hideColon = config.labelConfig.hideColon === true;
      const labelText = field.description || ''; // Pixel layouts do not need a colon by default

      const labelX = config.labelConfig.x !== undefined ? Number(config.labelConfig.x) : x;
      let labelY = config.labelConfig.y !== undefined ? Number(config.labelConfig.y) : y;
      const labelWidth = config.labelConfig.width !== undefined ? Number(config.labelConfig.width) : 100;
      const labelHeight = config.labelConfig.height !== undefined ? Number(config.labelConfig.height) : 20;

      const lFontSize = Number(config.labelConfig.fontSize) ||
        ((fieldType === 'Bool' || fieldType === 'CheckboxList') ? Number(config.fontSize) : 0) ||
        Number(labelFontSize) || 8;
      const lFont = config.labelConfig.isBold ? boldFont : font;
      const lColorString = config.labelConfig.fontColor || '#000000';
      const lColor = this.parseColor(lColorString);

      // Get alignment configuration (default to 'Left' for horizontal, 'Top' for vertical)
      const alignment = config.labelConfig.alignment || 'Left';
      const verticalAlignment = config.labelConfig.verticalAlignment || 'Top';  // Top, Middle, Bottom


      // Label Y Logic:
      // labelY is distance from Top of Page.
      // Text baseline should be roughly at (Top - labelY - fontSize).
      // We adjust slightly to align visually.

      // Heuristic Fix for "Label at Bottom" issue (e.g. LED_LIGHTS).
      // If LabelY (e.g. 810) is significantly different from Field Y (e.g. 50),
      // and LabelY is near bottom (> PageHeight/2), while Field is near top (< PageHeight/2),
      // Force LabelY to match Field Y.
      // Skip this fix when hideField is true, as the label should be positioned independently
      if (Math.abs(labelY - y) > 300 && config.hideField !== true) {
        // We can't assign to const. Recalculate labelPdfY using 'y' instead of 'labelY'.
        const fixedLabelPdfY = pageHeight - y - lFontSize + 2;

        // Truncate text if it exceeds the label width
        let truncatedText = labelText;
        let textWidth = lFont.widthOfTextAtSize(truncatedText, lFontSize);
        if (textWidth > labelWidth) {
          // Truncate character by character until it fits
          while (textWidth > labelWidth && truncatedText.length > 0) {
            truncatedText = truncatedText.slice(0, -1);
            textWidth = lFont.widthOfTextAtSize(truncatedText, lFontSize);
          }
        }

        // Calculate X position based on alignment
        let alignedX = labelX;
        if (alignment === 'Center') {
          alignedX = labelX + (labelWidth - textWidth) / 2;
        } else if (alignment === 'Right') {
          alignedX = labelX + labelWidth - textWidth;
        }

        // Calculate Y position based on vertical alignment
        // fixedLabelPdfY is baseline position at top of label area
        let verticallyAlignedY = fixedLabelPdfY;
        if (verticalAlignment === 'Middle') {
          // Center vertically: move down by half the remaining space after font
          verticallyAlignedY = fixedLabelPdfY - (labelHeight - lFontSize) / 2;
        } else if (verticalAlignment === 'Bottom') {
          // Align to bottom: move down by full remaining space
          verticallyAlignedY = fixedLabelPdfY - (labelHeight - lFontSize);
        }
        // 'Top' uses fixedLabelPdfY as-is

        // Use the fixed Y for drawing
        try {
          page.drawText(truncatedText, {
            x: alignedX,
            y: verticallyAlignedY,
            size: lFontSize,
            font: lFont,
            color: lColor,
          });
        } catch {
          // Ignore draw errors
        }

      } else {
        // Manual Multi-line Rendering to respect newlines and truncate instead of wrapping/squishing
        const lineHeight = lFontSize + 2;
        const lines = labelText.split(/\r?\n/);

        // Calculate total text height for vertical alignment
        const totalTextHeight = lines.length * lineHeight - 2; // Subtract spacing after last line

        // Calculate starting Y based on vertical alignment
        let startingPdfY = pageHeight - labelY - lFontSize + 2; // Default 'Top' alignment

        if (verticalAlignment === 'Middle') {
          // Center vertically: start higher by half the difference between label height and text height
          const verticalOffset = (labelHeight - totalTextHeight) / 2;
          startingPdfY = pageHeight - labelY - lFontSize + 2 - verticalOffset;
        } else if (verticalAlignment === 'Bottom') {
          // Bottom align: start at the bottom and work up
          const verticalOffset = labelHeight - totalTextHeight;
          startingPdfY = pageHeight - labelY - lFontSize + 2 - verticalOffset;
        }
        // 'Top' uses the default calculation

        let currentLinePdfY = startingPdfY;
        // Validate against bottom boundary: (pageHeight - labelY) is TOP of label box.
        // Bottom limit is (pageHeight - labelY - labelHeight).
        const backgroundBottomY = pageHeight - labelY - labelHeight;


        for (let i = 0; i < lines.length; i++) {
          // Check if next line fits vertically
          // The baseline is currentLinePdfY. The descent goes slightly below.
          // We'll use naive check: if baseline < backgroundBottomY + 2, stop.
          if (currentLinePdfY < backgroundBottomY) {
            break;
          }

          let line = lines[i];
          // Truncate horizontally if too wide
          // Naive iterative truncation for safety (could be optimized)
          let textWidth = lFont.widthOfTextAtSize(line, lFontSize);
          if (textWidth > labelWidth) {
            while (textWidth > labelWidth && line.length > 0) {
              line = line.slice(0, -1);
              textWidth = lFont.widthOfTextAtSize(line, lFontSize);
            }
            // Recalculate textWidth after truncation
            textWidth = lFont.widthOfTextAtSize(line, lFontSize);
          }

          // Calculate X position based on alignment
          let alignedX = labelX;
          if (alignment === 'Center') {
            alignedX = labelX + (labelWidth - textWidth) / 2;
          } else if (alignment === 'Right') {
            alignedX = labelX + labelWidth - textWidth;
          }
          // alignment === 'Left' uses labelX as-is

          try {
            page.drawText(line, {
              x: alignedX,
              y: currentLinePdfY,
              size: lFontSize,
              font: lFont,
              color: lColor,
            });
          } catch (err) {
          }

          currentLinePdfY -= lineHeight;
        }
      }
    } else if (!config.labelConfig && fieldType !== 'CheckboxList') {
      // Default label logic if no config provided (fallback) - usually for development
      // Only if config.labelConfig IS undefined (not null, which might mean 'hide')
      if (config.labelConfig === undefined) {
        const labelText = (field.description || '') + ':';
        const lFontSize = ((fieldType === 'Bool' || fieldType === 'CheckboxList') ? Number(config.fontSize) : 0) ||
          Number(labelFontSize) || 8;
        // Default to placing label above field? Or at same Y?
        // User's default logic seems to imply label is separate object effectively.
        // If we rely on absolute positioning, we shouldn't guess.
        // But LED_LIGHTS example had no labelConfig and user saw labels.
        // So we MUST implement default label rendering here.

        // Default: Place label at slightly adjusted geometry relative to field X/Y?
        // Actually, if labelConfig is missing, we usually assume label is NOT drawn or handled by separate Label object?
        // BUT user said LED_LIGHTS (no labelConfig) was "showing at bottom".
        // This implies we ARE drawing it.
        // Let's assume we use 'y' as labelY.

        const labelPdfY = pageHeight - y - lFontSize + 2;

        page.drawText(labelText, {
          x: x,
          y: labelPdfY,
          size: lFontSize,
          font: font,
          color: rgb(0, 0, 0)
        });
      }
    }

    // Check if field should be hidden (show only label)
    if (config.hideField === true) {
      return;
    }

    // Get value
    const rawValue = formData[field.code];
    // Use defaultValue if rawValue is undefined/null/empty string (but not false/0)
    let fieldValue = (rawValue !== undefined && rawValue !== null && rawValue !== '') ? rawValue :
      (config.defaultValue !== undefined ? config.defaultValue : '');

    // Handle parameterValue preloading if fieldValue is still empty
    if ((fieldValue === '' || fieldValue === undefined || fieldValue === null) && config.parameterValue) {
      const { source, property } = config.parameterValue;
      let dataSource: any = null;


      if (source === 'job') {
        dataSource = options.job;
        if (dataSource && property && dataSource[property] !== undefined && dataSource[property] !== null) {
          fieldValue = dataSource[property];
        }
      } else if (source === 'customer') {
        dataSource = options.customer;
        if (dataSource && property && dataSource[property] !== undefined && dataSource[property] !== null) {
          fieldValue = dataSource[property];
        }
      } else if (source === 'firstAppointment') {
        dataSource = options.firstAppointment;
        if (dataSource && property && dataSource[property] !== undefined && dataSource[property] !== null) {
          fieldValue = dataSource[property];
        }
      } else if (source === 'form') {
        if (options.formParameterValues && options.formParameterValues[field.id] !== undefined) {
          fieldValue = options.formParameterValues[field.id];
        }
      } else if (source === 'UserDataField') {
        // Check if we have userDataFields passed in options
        console.log(`[UDF_DEBUG] PDFService UserDataField lookup: property="${property}" userDataFields count=${options.userDataFields?.length ?? 0} available codes=[${(options.userDataFields || []).map((f: any) => f.businessDataField?.code).join(', ')}]`);
        if (options.userDataFields && options.userDataFields.length > 0) {
          const uField = options.userDataFields.find((f: any) => f.businessDataField?.code === property);
          if (uField) {
            fieldValue = uField.value;
            console.log(`[UDF_DEBUG] PDFService UserDataField MATCHED: property="${property}" value="${uField.value}"`);
          } else {
            console.log(`[UDF_DEBUG] PDFService UserDataField NOT FOUND: property="${property}"`);
          }
        }
      }
    }

    // Apply number formatting if configured
    if (config.numberFormat && fieldValue !== '' && fieldValue !== null && fieldValue !== undefined) {
      const { decimalPlaces = 2, useThousandsSeparator = true } = config.numberFormat;
      const numValue = typeof fieldValue === 'string' ? parseFloat(fieldValue.replace(/,/g, '')) : fieldValue;
      if (typeof numValue === 'number' && !isNaN(numValue)) {
        // Format with decimal places
        let formatted = numValue.toFixed(decimalPlaces);
        // Add thousand separators if enabled
        if (useThousandsSeparator) {
          const parts = formatted.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          formatted = parts.join('.');
        }
        fieldValue = formatted;
      }
    }

    // Create Widget
    // Based on fieldType
    switch (fieldType) {
      case 'Multiline':
        const multilineText = fieldValue ? fieldValue.toString() : '';
        const configuredFontSize = config.fontSize || defaultFontSize;

        // Rich text drawText path: only for non-interactive PDF output (download/print)
        if (multilineText && containsRichTextTags(multilineText) && !options.isInteractiveView) {
          // Draw border rectangle (replacing the form field border)
          page.drawRectangle({
            x,
            y: pdfY,
            width,
            height,
            borderWidth: 1,
            borderColor: rgb(0.7, 0.7, 0.7),
            color: rgb(1, 1, 1),
          });

          const segments = parseRichText(multilineText);
          const richMeasurer = createPdfRichTextMeasurer(font, boldFont);
          const lineHeightRatio = config.lineHeightRatio || DEFAULT_LINE_HEIGHT_RATIO;
          const autoResult = calculateFormattedAutoSizeFontSize({
            segments,
            maxWidth: width,
            maxHeight: height,
            maxFontSize: configuredFontSize,
            minFontSize: config.minFontSize || DEFAULT_MIN_FONT_SIZE,
            lineHeightRatio,
            measurer: richMeasurer
          });

          const baseFontSize = autoResult.baseFontSize;
          const padding = FIELD_PADDING / 2;
          // Start from top of field, descending
          let cursorY = pdfY + height - padding - baseFontSize;

          for (const line of autoResult.lines) {
            let cursorX = x + padding;
            for (const seg of line.segments) {
              const segFontSize = Math.max(1, baseFontSize + seg.style.sizeOffset);
              const segFont = seg.style.bold ? boldFont : font;
              try {
                page.drawText(seg.text, {
                  x: cursorX,
                  y: cursorY,
                  size: segFontSize,
                  font: segFont,
                  color: rgb(0, 0, 0),
                });
              } catch {
                // Ignore draw errors for unsupported characters
              }
              cursorX += richMeasurer.measureWidth(seg.text, segFontSize, seg.style.bold);
            }
            cursorY -= line.lineHeight;
          }
        } else {
          // Form field path: used for interactive view (all fields) and
          // non-interactive view (plain text fields without rich tags)
          let multilineField;
          try {
            multilineField = form.createTextField(field.code);
          }
          catch (err) {
            multilineField = form.getTextField(field.code);
          }

          multilineField.setText(multilineText);
          multilineField.enableMultiline();
          multilineField.addToPage(page, { x, y: pdfY, width, height, borderWidth: 1, borderColor: rgb(0.7, 0.7, 0.7) });

          let finalFontSize = configuredFontSize;

          if (multilineText) {
            const measurer = createPdfFontMeasurer(font);
            const autoSizeResult = calculateAutoSizeFontSize({
              text: multilineText,
              maxWidth: width,
              maxHeight: height,
              maxFontSize: configuredFontSize,
              minFontSize: config.minFontSize || DEFAULT_MIN_FONT_SIZE,
              lineHeightRatio: config.lineHeightRatio || DEFAULT_LINE_HEIGHT_RATIO,
              measurer
            });
            finalFontSize = autoSizeResult.fontSize;
          }

          try { multilineField.setFontSize(finalFontSize); } catch { }
        }
        break;

      case 'Bool':
        let checkBox;
        try { checkBox = form.createCheckBox(field.code); }
        catch { checkBox = form.getCheckBox(field.code); }

        // Re-use boolean check logic
        let shouldCheck = false;
        if (typeof fieldValue === 'boolean') { shouldCheck = fieldValue === true; }
        else if (typeof fieldValue === 'string') { shouldCheck = fieldValue.toLowerCase() === 'true' || fieldValue === '1'; }
        else if (typeof fieldValue === 'number') { shouldCheck = fieldValue === 1; }
        else { shouldCheck = !!fieldValue; }

        // Checkbox size is usually small (10x10). Center it in the box.
        const cbSize = 10;
        // Center horizontally if width > cbSize
        const cbX = x + (width > cbSize ? (width - cbSize) / 2 : 0);
        // Center vertically
        const cbYComputed = pdfY + (height > cbSize ? (height - cbSize) / 2 : 0);

        checkBox.addToPage(page, {
          x: cbX,
          y: cbYComputed,
          width: cbSize,
          height: cbSize,
          borderWidth: 1,
          borderColor: rgb(0, 0, 0)
        });
        // Only pre-check if not in interactive view mode (for download/print)
        if (!options.isInteractiveView && shouldCheck) checkBox.check();
        break;

      case 'CheckboxList':
        {
          const items = field.formFieldDataType?.formFieldDataItems?.filter((i: any) => i.isActive !== false) || [];
          const numColumns = config.checkboxColumns || options.checkboxListColumns || 3;

          // Handle Selected items
          let selectedCodes: string[] = [];
          if (fieldValue) {
            if (Array.isArray(fieldValue)) selectedCodes = fieldValue;
            else if (typeof fieldValue === 'string') {
              try { selectedCodes = JSON.parse(fieldValue); }
              catch { selectedCodes = fieldValue.split(',').map((s: string) => s.trim()); }
            }
          }

          // Render Logic
          const checkboxSize = 10;
          const colGap = 10;
          const colWidth = (width - (colGap * (numColumns - 1))) / numColumns;

          items.forEach((item: any, index: number) => {
            const colIndex = index % numColumns;
            const rowIndex = Math.floor(index / numColumns);

            // Calculate X
            const itemX = x + (colIndex * (colWidth + colGap));

            // Calculate Y - assume simple grid with 20pt rows
            const rowHeight = 20;
            // Start Y (top of field box)
            const startYTop = y;
            const itemYTop = startYTop + (rowIndex * rowHeight);

            // Convert to PDF Y (bottom-left)
            // Box for this item row: top=itemYTop, height=rowHeight.
            // PDF Y = pageHeight - itemYTop - rowHeight.
            // But let's center checkbox in the row.
            // Checkbox frame:
            const itemPdfY = pageHeight - itemYTop - rowHeight + (rowHeight - checkboxSize) / 2;

            // Checkbox Name
            const cbName = `${field.code}_${item.code}`;
            let cb;
            try { cb = form.createCheckBox(cbName); } catch { cb = form.getCheckBox(cbName); }

            cb.addToPage(page, {
              x: itemX,
              y: itemPdfY,
              width: 10,
              height: 10,
              borderWidth: 1,
              borderColor: rgb(0, 0, 0)
            });

            // Only pre-check if not in interactive view mode (for download/print)
            if (!options.isInteractiveView && selectedCodes.includes(item.code)) cb.check();

            // Label
            const lX = itemX + 13;
            const itemFontSize = Number(config.fontSize) || 8;
            const labelMaxWidth = colWidth - 13;
            const labelText = item.description || '';
            const itemLineHeight = itemFontSize + 2;

            // Manual wrapping
            const lines = this.wrapText(labelText, labelMaxWidth, font, itemFontSize);

            // Calculate starting Y to center text block vertically on checkbox
            const totalTextHeight = lines.length * itemLineHeight;
            const firstLineBaseline = itemPdfY + 5 + (totalTextHeight / 2) - itemFontSize;

            lines.forEach((line, lineIdx) => {
              page.drawText(line, {
                x: lX,
                y: firstLineBaseline - (lineIdx * itemLineHeight),
                size: itemFontSize,
                font,
                color: rgb(0, 0, 0)
              });
            });
          });
        }
        break;

      case 'TextboxWithCheckbox':
        {
          // Handle textbox with checkbox field type
          // Data format: { text: string, checked: boolean }
          let tbValue = '';
          let cbValue = false;

          if (fieldValue && typeof fieldValue === 'object') {
            tbValue = fieldValue.text || '';
            cbValue = fieldValue.checked || false;
          } else if (typeof fieldValue === 'string') {
            // Backward compatibility: treat string as just the text value
            tbValue = fieldValue;
            cbValue = false;
          }

          // Split available width: most for text field, small portion for checkbox area
          const cbWidth = 20;
          const gap = 5;
          const totalTextboxWidth = width - cbWidth - gap;

          const tbFontSize = config.fontSize || defaultFontSize;
          const prefix = config.prefix || '';
          const suffix = config.suffix || '';

          // Calculate affix widths
          const prefixWidth = prefix ? font.widthOfTextAtSize(prefix, tbFontSize) + 2 : 0;
          const suffixWidth = suffix ? font.widthOfTextAtSize(suffix, tbFontSize) + 2 : 0;

          const actualTbWidth = Math.max(0, totalTextboxWidth - prefixWidth - suffixWidth);
          const actualTbX = x + prefixWidth;

          // 1. Create text field
          let tbField;
          try {
            tbField = form.createTextField(`${field.code}_text`);
          } catch (error: any) {
            tbField = form.getTextField(`${field.code}_text`);
          }

          tbField.setText(tbValue ? tbValue.toString() : '');
          tbField.addToPage(page, {
            x: actualTbX,
            y: pdfY,
            width: actualTbWidth,
            height: height,
            borderWidth: 1,
            borderColor: rgb(0.7, 0.7, 0.7),
          });

          // Draw prefixes/suffixes as labels
          if (prefix) {
            page.drawText(prefix, {
              x: x,
              y: pdfY + (height - tbFontSize) / 2 + 1,
              size: tbFontSize,
              font: font,
              color: rgb(0, 0, 0)
            });
          }
          if (suffix) {
            page.drawText(suffix, {
              x: actualTbX + actualTbWidth + 2,
              y: pdfY + (height - tbFontSize) / 2 + 1,
              size: tbFontSize,
              font: font,
              color: rgb(0, 0, 0)
            });
          }

          try {
            tbField.setFontSize(tbFontSize);
          } catch (e) {
          }

          // 2. Create checkbox
          let cbField;
          const cbFieldName = `${field.code}_checkbox`;
          try {
            cbField = form.createCheckBox(cbFieldName);
          } catch (error: any) {
            cbField = form.getCheckBox(cbFieldName);
          }

          const cbSizeFinal = 10;
          const myCbX = x + totalTextboxWidth + gap + (cbWidth - cbSizeFinal) / 2;
          const myCbYComputed = pdfY + (height - cbSizeFinal) / 2;

          cbField.addToPage(page, {
            x: myCbX,
            y: myCbYComputed,
            width: cbSizeFinal,
            height: cbSizeFinal,
            borderWidth: 1,
            borderColor: rgb(0, 0, 0),
          });

          if (cbValue) {
            cbField.check();
          }
        }
        break;

      case 'Dimension':
        {
          // Parse dimension value (format: "H:value W:value D:value" or JSON)
          const parseDimVal = (val: string) => {
            if (!val) return { height: '', width: '', depth: '' };
            if (val.startsWith('{')) {
              try {
                const parsed = JSON.parse(val);
                return {
                  height: parsed.height || parsed.h || '',
                  width: parsed.width || parsed.w || '',
                  depth: parsed.depth || parsed.d || ''
                };
              } catch { }
            }
            const parts = val.split(' ');
            const result = { height: '', width: '', depth: '' };
            parts.forEach(part => {
              if (part.startsWith('H:')) result.height = part.substring(2);
              else if (part.startsWith('W:')) result.width = part.substring(2);
              else if (part.startsWith('D:')) result.depth = part.substring(2);
            });
            return result;
          };

          const dimensionValue = parseDimVal(fieldValue?.toString() || '');
          const dimensionLabels = ['H', 'W', 'D'];
          const vals = [dimensionValue.height, dimensionValue.width, dimensionValue.depth];

          const dFontSize = config.fontSize || defaultFontSize;
          const dGap = 5;
          const dPartWidth = (width - (dGap * 2)) / 3;

          for (let i = 0; i < 3; i++) {
            const dPartX = x + (i * (dPartWidth + dGap));
            const dSuffix = dimensionLabels[i];
            const dSuffixWidth = font.widthOfTextAtSize(dSuffix, dFontSize) + 2;
            const dTextFieldWidth = Math.max(0, dPartWidth - dSuffixWidth - 1);

            const dFieldName = `${field.code}_${dSuffix}`;
            let dField;
            try {
              dField = form.createTextField(dFieldName);
            } catch {
              dField = form.getTextField(dFieldName);
            }

            dField.setText((vals[i] !== undefined && vals[i] !== null) ? vals[i].toString() : '');
            dField.addToPage(page, {
              x: dPartX,
              y: pdfY,
              width: dTextFieldWidth,
              height: height,
              borderWidth: 1,
              borderColor: rgb(0.7, 0.7, 0.7),
            });
            try {
              dField.setFontSize(dFontSize);
            } catch { }

            page.drawText(dSuffix, {
              x: dPartX + dTextFieldWidth + 2,
              y: pdfY + (height - dFontSize) / 2 + 1,
              size: dFontSize,
              font: font,
              color: rgb(0, 0, 0)
            });
          }
        }
        break;

      case 'HorizontalRule':
        {
          // Draw a horizontal line across the available width
          // The thickness should equal the height of the field.
          const ruleY = pdfY + (height / 2); // Center of the field box in PDF coordinates

          page.drawLine({
            start: { x: x, y: ruleY },
            end: { x: x + width, y: ruleY },
            thickness: height,
            color: rgb(0.7, 0.7, 0.7), // Use gray for the rule to match standard dividers
          });
        }
        break;

      case 'Date':
        // Handle Date fields with dd-MM-yyyy format
        let dateField;
        try { dateField = form.createTextField(field.code); }
        catch { dateField = form.getTextField(field.code); }

        const dateFontSize = config.fontSize || defaultFontSize;
        const datePrefix = config.prefix || '';
        const dateSuffix = config.suffix || '';

        // Calculate heights/widths
        const datePrefixWidth = datePrefix ? font.widthOfTextAtSize(datePrefix, dateFontSize) + 2 : 0;
        const dateSuffixWidth = dateSuffix ? font.widthOfTextAtSize(dateSuffix, dateFontSize) + 2 : 0;

        const dateActualFieldWidth = Math.max(0, width - datePrefixWidth - dateSuffixWidth);
        const dateActualFieldX = x + datePrefixWidth;

        // Format date value
        let formattedDateValue = '';
        if (fieldValue) {
          try {
            const date = new Date(fieldValue);
            formattedDateValue = format(date, 'dd/MM/yyyy');
          } catch (e) {
            formattedDateValue = fieldValue.toString();
          }
        }

        dateField.setText(formattedDateValue);
        dateField.addToPage(page, {
          x: dateActualFieldX,
          y: pdfY,
          width: dateActualFieldWidth,
          height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7)
        });

        // Draw labels
        if (datePrefix) {
          page.drawText(datePrefix, {
            x: x,
            y: pdfY + (height - dateFontSize) / 2 + 1,
            size: dateFontSize,
            font: font,
            color: rgb(0, 0, 0)
          });
        }
        if (dateSuffix) {
          page.drawText(dateSuffix, {
            x: dateActualFieldX + dateActualFieldWidth + 2,
            y: pdfY + (height - dateFontSize) / 2 + 1,
            size: dateFontSize,
            font: font,
            color: rgb(0, 0, 0)
          });
        }

        try { dateField.setFontSize(dateFontSize); } catch { }
        break;

      case 'DateTime':
        // Handle DateTime fields with dd-MM-yyyy h:mma format
        let dateTimeField;
        try { dateTimeField = form.createTextField(field.code); }
        catch { dateTimeField = form.getTextField(field.code); }

        const dateTimeFontSize = config.fontSize || defaultFontSize;
        const dateTimePrefix = config.prefix || '';
        const dateTimeSuffix = config.suffix || '';

        // Calculate heights/widths
        const dateTimePrefixWidth = dateTimePrefix ? font.widthOfTextAtSize(dateTimePrefix, dateTimeFontSize) + 2 : 0;
        const dateTimeSuffixWidth = dateTimeSuffix ? font.widthOfTextAtSize(dateTimeSuffix, dateTimeFontSize) + 2 : 0;

        const dateTimeActualFieldWidth = Math.max(0, width - dateTimePrefixWidth - dateTimeSuffixWidth);
        const dateTimeActualFieldX = x + dateTimePrefixWidth;

        // Format datetime value
        let formattedDateTimeValue = '';
        if (fieldValue) {
          try {
            const date = new Date(fieldValue);
            formattedDateTimeValue = format(date, 'dd/MM/yyyy h:mma');
          } catch (e) {
            formattedDateTimeValue = fieldValue.toString();
          }
        }

        dateTimeField.setText(formattedDateTimeValue);
        dateTimeField.addToPage(page, {
          x: dateTimeActualFieldX,
          y: pdfY,
          width: dateTimeActualFieldWidth,
          height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7)
        });

        // Draw labels
        if (dateTimePrefix) {
          page.drawText(dateTimePrefix, {
            x: x,
            y: pdfY + (height - dateTimeFontSize) / 2 + 1,
            size: dateTimeFontSize,
            font: font,
            color: rgb(0, 0, 0)
          });
        }
        if (dateTimeSuffix) {
          page.drawText(dateTimeSuffix, {
            x: dateTimeActualFieldX + dateTimeActualFieldWidth + 2,
            y: pdfY + (height - dateTimeFontSize) / 2 + 1,
            size: dateTimeFontSize,
            font: font,
            color: rgb(0, 0, 0)
          });
        }

        try { dateTimeField.setFontSize(dateTimeFontSize); } catch { }
        break;

      default:
        // Default text field
        let textField;
        try { textField = form.createTextField(field.code); }
        catch { textField = form.getTextField(field.code); }

        const fieldFontSize = config.fontSize || defaultFontSize;
        const prefix = config.prefix || '';
        const suffix = config.suffix || '';

        // Calculate heights/widths
        const prefixWidth = prefix ? font.widthOfTextAtSize(prefix, fieldFontSize) + 2 : 0;
        const suffixWidth = suffix ? font.widthOfTextAtSize(suffix, fieldFontSize) + 2 : 0;

        const actualFieldWidth = Math.max(0, width - prefixWidth - suffixWidth);
        const actualFieldX = x + prefixWidth;

        textField.setText(fieldValue ? fieldValue.toString() : '');
        textField.addToPage(page, {
          x: actualFieldX,
          y: pdfY,
          width: actualFieldWidth,
          height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7)
        });

        // Draw labels
        if (prefix) {
          page.drawText(prefix, {
            x: x,
            y: pdfY + (height - fieldFontSize) / 2 + 1,
            size: fieldFontSize,
            font: font,
            color: rgb(0, 0, 0)
          });
        }
        if (suffix) {
          page.drawText(suffix, {
            x: actualFieldX + actualFieldWidth + 2,
            y: pdfY + (height - fieldFontSize) / 2 + 1,
            size: fieldFontSize,
            font: font,
            color: rgb(0, 0, 0)
          });
        }

        try { textField.setFontSize(fieldFontSize); } catch { }
        break;
    }
  }
}
